import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, getDocs, orderBy } from 'firebase/firestore';
import AccessNotice from '../../components/Portal/AccessNotice';
import { isPermissionDenied } from '../../utils/permissions';

// Coerce a Firestore Timestamp / Date / ISO string to a JS Date (or null).
// Portal-written docs store createdAt as a Timestamp, which new Date() renders
// as "Invalid Date" — coerce it first (#48).
const toJsDate = (val) => {
	if (!val) return null;
	if (typeof val.toDate === 'function') return val.toDate();
	if (val instanceof Date) return val;
	const d = new Date(val);
	return isNaN(d) ? null : d;
};

const MaterialRequests = () => {
	const [requests, setRequests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [permDenied, setPermDenied] = useState(false);

	useEffect(() => {
		const fetchRequests = async () => {
			try {
				// Fetch all material requests
				const requestsQuery = query(
					collection(db, 'materialRequests'),
					orderBy('createdAt', 'desc')
				);
				const querySnapshot = await getDocs(requestsQuery);
				const requestsData = querySnapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));
				setRequests(requestsData);
			} catch (error) {
				console.error('Error fetching requests:', error);
				// A rules denial must not read as "No material requests" (#27).
				if (isPermissionDenied(error)) setPermDenied(true);
			} finally {
				setLoading(false);
			}
		};

		fetchRequests();
	}, []);

	// Returns a .ps-badge--* modifier (portalSurface.css). Was a Web Awesome
	// `variant`; the values are identical, so only the consumer changed.
	const getStatusVariant = (status) => {
		switch (status) {
			case 'completed':
				return 'success';
			case 'in_progress':
				return 'warning';
			default:
				return 'neutral';
		}
	};

	if (loading) {
		return (
			<div className="ps-page">
				<div className="ps-spinner" role="status" aria-label="Loading" />
			</div>
		);
	}

	return (
		<div className="ps-page">
			<h1 className="ps-h2">Material Requests</h1>

			{permDenied ? (
				<AccessNotice what="material requests" />
			) : requests.length === 0 ? (
				<div className="ps-empty">
					<strong>No material requests</strong>
					<p>
						Customer material requests will appear here once they are
						submitted.
					</p>
				</div>
			) : (
				<div className="ps-table-wrap">
					<table className="ps-table">
							<thead>
								<tr>
									<th>Date</th>
									<th>Student</th>
									<th>Type</th>
									<th>Status</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{requests.map((request) => (
									<tr key={request.id}>
										<td>
											{toJsDate(request.createdAt)
												? toJsDate(request.createdAt).toLocaleDateString()
												: '—'}
										</td>
										<td>{request.studentEmail}</td>
										<td>{request.type}</td>
										<td>
											<span className={`ps-badge ps-badge--${getStatusVariant(request.status)}`}>
												{request.status}
											</span>
										</td>
										<td>
											<button type="button" className="ps-btn">
												View Details
											</button>
										</td>
									</tr>
								))}
							</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default MaterialRequests;
